import {
  type ColumnFiltersState,
  type ExpandedState,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
  type TableFeatures,
} from '@tanstack/react-table';
import {
  type LegacyColumnDef as ColumnDef,
  type LegacyTableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useLegacyTable,
} from '@tanstack/react-table/legacy';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '#components/Input/IconButton';
import { Chevron } from '#icons/Chevron';
import { SpinnerOverlay } from '#components/Feedback/SpinnerOverlay';
import { type breakpoint } from '../../../themes/stena-recycling/breakpoint';
import { OuterTableContainer, StyledTable, TableBody, TableContainer, TableWrapper } from './style';
import { Pagination } from './subs/Pagination';
import { LoadMore } from './subs/LoadMore';
import { TableEmpty } from './subs/TableEmpty';
import { LoadingIndicator } from './subs/LoadingIndicator';
import { TableHeader } from './subs/TableHeader';
import { TableRows } from './subs/TableRows';
import { ColGroup } from './subs/ColGroup';

export { TableEmpty as TableEmptyV2 } from './subs/TableEmpty';

declare module '@tanstack/react-table' {
  interface FilterValueReplacement {
    value: string | number;
    replacementValueToDisplay: string | number;
    prioritize?: boolean;
  }

  //allows us to define custom properties for our columns
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TFeatures extends TableFeatures, TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select';
    tooltip?: ReactNode;
    customTooltip?: ReactNode;
    align?: 'left' | 'right';
    slim?: boolean;
    allowFilterSearch?: boolean; // Show search input in select filter
    allowFilterToggleAll?: boolean; // Show "All" option in select filter
    persistFilter?: boolean; // If true, the filter selections will persist when the data passed into the table changes
    replaceFilterValue?: FilterValueReplacement; // Replace specific filter value with a different display value
  }
}

export type TableV2Props<TData extends RowData> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];

  allowFiltering?: boolean;
  pagination?: boolean;
  expandable?: boolean;
  expandableRowClick?: boolean; // When true, entire row is clickable instead of button
  expandedState?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  emptyState?: React.ReactNode;
  loading?: boolean;

  meta?: LegacyTableOptions<TData>['meta'];
  sorting?: boolean;
  sortingCallback?: OnChangeFn<SortingState>;
  sortingState?: SortingState;
  paginationState?: PaginationState;
  fetchingNextPage?: boolean;
  initialState?: LegacyTableOptions<TData>['initialState'];
  state?: LegacyTableOptions<TData>['state'];
  verticalAlign?: 'top' | 'middle' | 'bottom';
  loadMore?: boolean;
  onLoadMore?: () => void;
  loadMoreIsLoading?: boolean;
  hasMoreToLoad?: boolean;
  stickyHeader?: boolean;
  responsiveBreakpoint?: keyof typeof breakpoint;
  infiniteScroll?: boolean;
  expandableButtonPosition?: 'first' | 'last';
  expandableButtonShow?: boolean;

  /**
   * If this function is provided, it will be called when the pagination state changes and you will be expected to manage the state yourself. You can pass the managed state back to the table via the `tableOptions.state.pagination` option.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/pagination#onpaginationchange)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
   */
  onPaginationChange?: OnChangeFn<PaginationState>;
  /**
   * When manually controlling pagination, you can supply a total `pageCount` value to the table if you know it (Or supply a `rowCount` and `pageCount` will be calculated). If you do not know how many pages there are, you can set this to `-1`.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/pagination#pagecount)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
   */
  pageCount?: number;
  /**
   * When manually controlling pagination, you can supply a total `rowCount` value to the table if you know it. The `pageCount` can be calculated from this value and the `pageSize`.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/pagination#rowcount)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
   */
  rowCount?: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const expandColumn = <TData extends RowData>(): ColumnDef<TData, any> => ({
  id: 'select',
  size: 64,
  cell: ({ row }) => (
    <IconButton
      variant="secondary"
      onClick={() => {
        row.toggleExpanded();
      }}
      data-testid="expand-accordion"
    >
      {row.getIsExpanded() ? <Chevron rotation={180} /> : <Chevron />}
    </IconButton>
  ),
});

const getDefaultColumns = <TData extends RowData>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultColumns: ColumnDef<TData, any>[],
  expandable: boolean,
  expandableRowClick: boolean,
  expandableButtonPosition: TableV2Props<TData>['expandableButtonPosition'],
  expandableButtonShow: TableV2Props<TData>['expandableButtonShow'],

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<TData, any>[] => {
  if (
    !expandable ||
    (expandableRowClick && !expandableButtonShow) ||
    expandableButtonShow === false
  ) {
    return defaultColumns;
  }

  if (expandableButtonPosition === 'first') {
    return [expandColumn(), ...defaultColumns];
  }

  return [...defaultColumns, expandColumn()];
};

/**
 * TableV2
 * @param allowFiltering: boolean - default false
 */
export const TableV2 = <TData extends RowData>({
  columns: defaultColumns,
  data,
  allowFiltering,
  pagination,
  expandable = false,
  expandableRowClick = false,
  expandedState,
  onExpandedChange,
  sorting = true,
  stickyHeader,
  sortingCallback,
  sortingState,
  emptyState = <TableEmpty />,
  loading,
  meta,
  pageCount,
  rowCount,
  onPaginationChange,
  paginationState,
  fetchingNextPage = false,
  verticalAlign = 'middle',
  loadMore = false,
  onLoadMore,
  hasMoreToLoad = false,
  loadMoreIsLoading = false,
  infiniteScroll = false,
  state = {},
  initialState = {},
  responsiveBreakpoint = 'large',
  expandableButtonPosition = 'last',
  expandableButtonShow,
}: TableV2Props<TData>) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});

  const [tableContainer, setTableContainer] = useState<HTMLDivElement | null>(null);
  const wasLoadingRef = useRef(false);

  // Use controlled expansion state if provided, otherwise use internal state
  const isExpandedControlled = expandedState !== undefined && onExpandedChange !== undefined;
  const expanded = isExpandedControlled ? expandedState : internalExpanded;
  const setExpanded = isExpandedControlled ? onExpandedChange : setInternalExpanded;
  const columns = useMemo(
    () =>
      getDefaultColumns(
        defaultColumns,
        expandable,
        expandableRowClick,
        expandableButtonPosition,
        expandableButtonShow,
      ),
    [
      defaultColumns,
      expandable,
      expandableRowClick,
      expandableButtonPosition,
      expandableButtonShow,
    ],
  );
  const [columnVisibility, setColumnVisibility] = useState({});

  const manualPagination = (pagination && !!onPaginationChange) || loadMore;

  const table = useLegacyTable<TData>({
    data,
    columns,
    defaultColumn: {
      size: -1,
    },
    filterFns: {},
    initialState,
    state: {
      columnFilters,
      columnVisibility,
      expanded,
      ...(sortingState ? { sorting: sortingState } : {}),
      ...(paginationState ? { pagination: paginationState } : {}),
      ...state,
    },
    manualPagination,
    pageCount,
    rowCount,
    enableSorting: sorting,
    enableMultiSort: false,
    getRowCanExpand: () => expandable,
    manualSorting: !!sortingCallback,
    onExpandedChange: setExpanded,
    getExpandedRowModel: expandable ? getExpandedRowModel() : undefined,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), //client side filtering
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    meta,
    ...(sortingCallback ? { onSortingChange: sortingCallback } : {}),
    ...(onPaginationChange ? { onPaginationChange } : {}),
  });

  const rowModel = table.getRowModel();

  const hasRows = rowModel.rows.length > 0;

  useEffect(() => {
    const startedLoading = !!loading && !wasLoadingRef.current;

    wasLoadingRef.current = !!loading;

    if (startedLoading) {
      table.resetColumnFilters();
    }
  }, [loading, table]);

  const tableSorting = table.getState().sorting;
  const columnsWithVisible = table.getAllColumns().filter((column) => column.getIsVisible());
  const visibleColumnIds = columnsWithVisible.map((column) => column.id).join('|');

  return (
    <TableWrapper translate="no">
      <SpinnerOverlay active={fetchingNextPage} />
      <OuterTableContainer>
        <TableContainer stickyHeader={stickyHeader} ref={setTableContainer}>
          <StyledTable responsiveBreakpoint={responsiveBreakpoint}>
            <ColGroup columns={table.getVisibleFlatColumns()} />
            <TableHeader
              key={JSON.stringify(tableSorting)} //force re-render of header when sorting changes to update sorting icons
              headerGroups={table.getHeaderGroups()}
              sorting={sorting}
              allowFiltering={allowFiltering}
              stickyHeader={stickyHeader}
            />
            <TableBody>
              <LoadingIndicator
                loading={!!loading}
                columnsLength={table.getVisibleFlatColumns().length}
              />
              {!loading && (
                <TableRows
                  rows={table.getRowModel().rows}
                  expandable={expandable}
                  expandableRowClick={expandableRowClick}
                  verticalAlign={verticalAlign}
                  infiniteScroll={infiniteScroll}
                  tableContainer={tableContainer}
                  loadMore={onLoadMore}
                  isLoadingMore={loadMoreIsLoading}
                  hasMoreToLoad={hasMoreToLoad}
                  key={visibleColumnIds} // force re-render of rows when column visibility changes to ensure correct virtualization
                />
              )}

              <LoadingIndicator
                loading={loadMoreIsLoading}
                columnsLength={table.getVisibleFlatColumns().length}
              />
            </TableBody>
          </StyledTable>
          {!hasRows && !loading && emptyState}
          {hasMoreToLoad && onLoadMore && !infiniteScroll && (
            <LoadMore onLoadMore={onLoadMore} isLoading={loadMoreIsLoading} />
          )}
        </TableContainer>
      </OuterTableContainer>

      {!loading && pagination && <Pagination table={table} />}
    </TableWrapper>
  );
};
